// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld/base/core';
import L from 'leaflet';

myw.MapInteractionMode = myw.MywClass.extend({
    /**
     * @class  Specifies what a MapInteractionMode class should implement.
     * Subclass and use with {MapControl#setInteractionMode} to change the handling of user interactions with the map, such as mouse clicks
     * @param  {MapControl} map
     * @constructs
     */
    initialize(map) {
        this.map = map;
    },

    /** to be called when the mode is enabled */
    enable() {},

    /** to be called when the mode is disabled (includes when being replaced by another mode ) */
    disable() {},

    /** to be called when the map is clicked
     * @param  {MouseEvent} event Mouse click event
     */
    handleMapClick(event) {
        return true; //do nothing but report as handled
    },

    /**
     * Called from map control. Stops click event propagating to Map
     */
    handleFeatureClick(feature, rep, evt) {
        L.DomEvent.stopPropagation(evt);
    },

    /**
     * Returns context menu item configurations for an list of actions
     * @param   array {strings}  actions  list of actions to be processed
     * @param   {integer}        base     (optional) initial menu index to be used
     * @return  array {objects}  menu item configurations for the actions
     */
    buildActionMenuItems(actions, base) {
        actions = actions || [];
        base = base || 0;

        const menuItems = actions
            .map(this._parseAction.bind(this))
            .filter(this._isActionAvailable.bind(this))
            .map((e, i) => this._getOptionsForAction(e, base + i));
        return menuItems;
    },

    /**
     * Returns component and actionId for an action string
     * @param  {string}  action
     * @return {object}  parts with component and actionId
     * @private
     */
    _parseAction(action, showOutput = true) {
        const parts = action.split('.');
        let component;
        let actionId;

        if (parts.length > 1) {
            //application or plugin action
            const pluginId = parts[0];

            actionId = parts[1];
            if (pluginId == 'application') component = this.app;
            else component = this.app.plugins[pluginId];
            if (!component && showOutput)
                console.log("No plugin '" + pluginId + "' available for '" + action + "'");
        } else {
            component = this.map;
            actionId = action;
        }

        return { component: component, actionId: actionId };
    },

    /**
     * Returns true if an action is available
     * @param  {object}  action with component and actionId
     * @return {boolean}
     * @private
     */
    _isActionAvailable(action) {
        const component = action.component;
        const actionId = action.actionId;

        if (actionId == '-') return true;
        if (!component) return false;

        let actionDef;
        actionDef = component.actions && component.actions[actionId];

        if (!actionDef) {
            if (!component) console.log("No action '" + actionId + "'"); //ENH: Show name of component in message
        }

        return typeof actionDef.available == 'undefined' || _.result(actionDef, 'available');
    },

    /**
     * Returns the context menu item configuration for a given action
     * @param  {object}  action with component and actionId
     * @param  {integer} index  Index of the action in the menu
     * @return {object}        Check https://github.com/aratcliffe/Leaflet.contextmenu#usage
     * @private
     */
    _getOptionsForAction(action, index) {
        const component = action.component;
        const actionId = action.actionId;
        if (actionId == '-') return actionId;

        let actionDef, checked, iconSrc;
        actionDef = component.actions && component.actions[actionId];

        checked = actionDef && _.result(component, actionDef.checked);
        iconSrc = checked
            ? 'images/activeContextMenuItem.png'
            : 'images/inactiveContextMenuItem.png';

        return {
            text: component.msg('context_menu_' + actionId),
            icon: myw.baseUrl + iconSrc,
            callback: this._executePluginContextMenuAction.bind(
                this,
                component,
                action,
                actionDef,
                index
            )
        };
    },

    /**
     * Executes a context menu plugin action.
     * The menu item is replaced so it can reflect a new state (if necessary)
     * @param  {object}     component
     * @param  {string}     action
     * @param  {object}     actionDef
     * @param  {integer}    index
     * @param  {L.LatLng}   location.latlng
     * @param  {L.Point}    location.containerPoint
     * @param  {L.Point}    location.layerPoint
     * @private
     */
    _executePluginContextMenuAction(component, action, actionDef, index, location) {
        const methodName = actionDef && actionDef.action;
        let options;

        if (component[methodName]) {
            //execute the action
            component[methodName](location);

            options = this._getOptionsForAction(action, index);

            this.map.contextmenu.removeItem(index);
            this.map.contextmenu.insertItem(options, index);
        } else {
            console.log("No method '" + methodName + "' for action '" + action + "'");
        }
    }
});

/**
 * Details of a map mouse event. {@link http://leafletjs.com/reference.html#mouse-event}
 * @typedef MouseEvent
 */

export default myw.MapInteractionMode;
